import React, { useRef, useEffect } from 'react';
import { Box, Typography, Paper, Stack } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import { ChatMessage } from '../../types/unifiedSchema';
import ChatSuggestionCard from './ChatSuggestionCard';

interface ChatMessageListProps {
  messages: ChatMessage[];
  isTyping: boolean;
  onApplySuggestion: (messageId: string, suggestionId: string) => void;
  onRejectSuggestion: (messageId: string, suggestionId: string) => void;
}

/** Markdown-aware styles for assistant bubbles */
const markdownSx = {
  '& p': { m: 0, mb: 0.5, lineHeight: 1.5, '&:last-child': { mb: 0 } },
  '& h1, & h2, & h3, & h4': {
    mt: 1,
    mb: 0.5,
    fontWeight: 600,
    lineHeight: 1.3,
  },
  '& h3': { fontSize: '0.95rem' },
  '& h4': { fontSize: '0.875rem' },
  '& ul, & ol': { m: 0, pl: 2.5, mb: 0.5 },
  '& li': { mb: 0.25, fontSize: '0.8125rem', lineHeight: 1.5 },
  '& li > p': { display: 'inline' },
  '& code': {
    fontFamily: '"Fira Mono", "Roboto Mono", monospace',
    fontSize: '0.78rem',
    bgcolor: 'rgba(0,0,0,0.06)',
    px: 0.5,
    py: 0.15,
    borderRadius: 0.5,
  },
  '& pre': {
    bgcolor: 'rgba(0,0,0,0.06)',
    borderRadius: 1,
    p: 1,
    overflowX: 'auto',
    mb: 0.5,
    '& code': { bgcolor: 'transparent', p: 0 },
  },
  '& strong': { fontWeight: 600 },
  '& em': { fontStyle: 'italic' },
  '& hr': { my: 1, border: 0, borderTop: '1px solid', borderColor: 'divider' },
  '& table': {
    width: '100%',
    borderCollapse: 'collapse',
    mb: 0.5,
    fontSize: '0.78rem',
  },
  '& th, & td': {
    border: '1px solid',
    borderColor: 'divider',
    px: 1,
    py: 0.5,
    textAlign: 'left',
  },
  '& th': { fontWeight: 600, bgcolor: 'rgba(0,0,0,0.03)' },
};

const ChatMessageList: React.FC<ChatMessageListProps> = ({
  messages,
  isTyping,
  onApplySuggestion,
  onRejectSuggestion,
}) => {
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  return (
    <Box
      sx={{
        flex: 1,
        overflowY: 'auto',
        px: 2,
        py: 1,
      }}
    >
      {messages.length === 0 && (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          height="100%"
          flexDirection="column"
          py={4}
        >
          <Typography variant="body2" color="text.secondary" textAlign="center">
            Ask questions about your schema, request changes, or get suggestions.
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ mt: 1 }}>
            e.g., "What should the primary key of orders be?"
          </Typography>
        </Box>
      )}

      {messages.map((msg) => {
        const isUser = msg.role === 'user';

        return (
          <Box
            key={msg.id}
            sx={{
              display: 'flex',
              justifyContent: isUser ? 'flex-end' : 'flex-start',
              mb: 1.5,
            }}
          >
            <Paper
              elevation={0}
              sx={{
                px: 2,
                py: 1,
                maxWidth: '85%',
                bgcolor: msg.error ? '#ffebee' : (isUser ? '#e3f2fd' : 'grey.50'),
                color: msg.error ? 'error.main' : 'text.primary',
                borderRadius: 2,
                borderTopRightRadius: isUser ? 0 : undefined,
                borderTopLeftRadius: (!isUser || msg.error) ? 0 : undefined,
                border: '1px solid',
                borderColor: msg.error ? 'error.main' : (isUser ? '#90caf9' : 'grey.200'),
              }}
            >
              {isUser ? (
                /* User messages — plain text, no markdown */
                <Typography
                  variant="body2"
                  sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                >
                  {msg.content}
                </Typography>
              ) : (
                /* Assistant messages — rendered markdown or error message */
                msg.error ? (
                  /* Error messages — plain text with error styling */
                  <Typography
                    variant="body2"
                    sx={{ 
                      whiteSpace: 'pre-wrap', 
                      wordBreak: 'break-word',
                      fontWeight: 500,
                    }}
                  >
                    {msg.content}
                  </Typography>
                ) : (
                  /* Normal assistant messages — rendered markdown */
                  <Box
                    sx={{
                      fontSize: '0.8125rem',
                      wordBreak: 'break-word',
                      ...markdownSx,
                    }}
                  >
                    <ReactMarkdown>{msg.content}</ReactMarkdown>
                  </Box>
                )
              )}

              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  mt: 0.5,
                  opacity: 0.55,
                  textAlign: isUser ? 'right' : 'left',
                  color: 'text.secondary',
                }}
              >
                {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </Typography>

              {msg.suggestions && msg.suggestions.length > 0 && (
                <Stack spacing={1} sx={{ mt: 1 }}>
                  {msg.suggestions.map((suggestion) => (
                    <ChatSuggestionCard
                      key={suggestion.id}
                      suggestion={suggestion}
                      onAccept={() => onApplySuggestion(msg.id, suggestion.id)}
                      onReject={() => onRejectSuggestion(msg.id, suggestion.id)}
                    />
                  ))}
                </Stack>
              )}
            </Paper>
          </Box>
        );
      })}

      {isTyping && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1.5 }}>
          <Paper
            elevation={0}
            sx={{
              px: 2,
              py: 1,
              bgcolor: 'grey.50',
              borderRadius: 2,
              borderTopLeftRadius: 0,
              border: '1px solid',
              borderColor: 'grey.200',
            }}
          >
            <Stack direction="row" spacing={0.5} alignItems="center">
              {[0, 1, 2].map((i) => (
                <Box
                  key={i}
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: 'text.secondary',
                    animation: 'typingDot 1.4s infinite',
                    animationDelay: `${i * 0.2}s`,
                    '@keyframes typingDot': {
                      '0%, 60%, 100%': { opacity: 0.3 },
                      '30%': { opacity: 1 },
                    },
                  }}
                />
              ))}
            </Stack>
          </Paper>
        </Box>
      )}

      <div ref={bottomRef} />
    </Box>
  );
};

export default ChatMessageList;
